/*
 * Dashboard.jsx
 * -------------
 * Main authenticated user dashboard.
 * Shows a greeting, lifetime stats row, streak summary,
 * and four sections of items: Overdue, Today, Upcoming, No Date.
 * Items are sorted by date then priority within each section.
 * Fetches all checklists on mount and derives section data client side.
 * Consumes AuthContext and NotificationContext.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useNotifications } from "../App";
import StatsCard from "../components/StatsCard";
import ChecklistItem from "../components/ChecklistItem";
import StreakBadge from "../components/StreakBadge";

// ============================================
// HELPERS
// ============================================
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function getPriorityWeight(priority) {
  const weights = { high: 0, medium: 1, low: 2 };
  return weights[priority] ?? 2;
}

function categorizeItems(checklists) {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const next7Days = new Date(now);
  next7Days.setDate(now.getDate() + 7);

  const overdue = [];
  const today = [];
  const upcoming = [];
  const noDate = [];

  for (const checklist of checklists) {
    for (const item of checklist.items || []) {
      if (item.checked) continue;

      const enriched = { ...item, checklistTitle: checklist.title, checklistId: checklist.id };

      if (!item.dueDate) {
        noDate.push(enriched);
        continue;
      }

      const due = new Date(item.dueDate);
      if (due < todayStart) {
        overdue.push(enriched);
      } else if (due >= todayStart && due <= todayEnd) {
        today.push(enriched);
      } else if (due > todayEnd && due <= next7Days) {
        upcoming.push(enriched);
      }
    }
  }

  // Sort each section by date then priority
  const sortByPriority = (a, b) => getPriorityWeight(a.priority) - getPriorityWeight(b.priority);
  const sortByDateThenPriority = (a, b) => {
    const dateDiff = new Date(a.dueDate) - new Date(b.dueDate);
    if (dateDiff !== 0) return dateDiff;
    return sortByPriority(a, b);
  };

  overdue.sort(sortByDateThenPriority);
  today.sort(sortByPriority);
  upcoming.sort(sortByDateThenPriority);
  noDate.sort(sortByPriority);

  return { overdue, today, upcoming, noDate };
}

function computeStats(checklists) {
  let totalCompleted = 0;
  let totalItems = 0;
  let bestStreak = 0;
  const activeStreaks = [];

  for (const checklist of checklists) {
    for (const item of checklist.items || []) {
      totalItems++;
      if (item.checked) totalCompleted++;
    }
    if (checklist.streak > bestStreak) bestStreak = checklist.streak;
    if (checklist.streak > 0) activeStreaks.push(checklist);
  }

  const completionRate = totalItems === 0 ? 0 : Math.round((totalCompleted / totalItems) * 100);

  return { totalCompleted, totalItems, bestStreak, completionRate, activeStreaks };
}

// ============================================
// SECTION HEADER
// Collapsible section title with item count
// ============================================
function SectionHeader({ title, count, color, collapsed, onToggle }) {
  if (count === 0) return null;

  return (
    <button
      onClick={onToggle}
      style={{
        width: "100%",
        background: "none",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "var(--space-3) 0",
        borderBottom: "2px solid " + color
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <h2 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: "20px",
          color,
          margin: 0
        }}>
          {title}
        </h2>
        <span style={{
          fontFamily: "var(--font-body)",
          fontSize: "13px",
          fontWeight: 600,
          color: "white",
          background: color,
          borderRadius: "100px",
          padding: "2px 8px"
        }}>
          {count}
        </span>
      </div>
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        style={{
          color,
          transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
          transition: "transform var(--transition-base)"
        }}
      >
        <path d="M3 6L8 11L13 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}

// ============================================
// ACTIVE STREAKS SUMMARY
// ============================================
function StreakSummary({ checklists }) {
  const activeStreaks = checklists.filter(c => c.streak > 0);
  if (activeStreaks.length === 0) return null;

  return (
    <div style={{
      background: "var(--accent-gold-muted)",
      border: "1px solid var(--accent-gold)",
      borderRadius: "var(--radius-md)",
      padding: "var(--space-4) var(--space-5)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-3)"
    }}>
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "12px",
        fontWeight: 600,
        color: "var(--accent-gold)",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        margin: 0
      }}>
        Active Streaks
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
        {activeStreaks.map(checklist => (
          <div key={checklist.id} style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)"
          }}>
            <span style={{
              fontFamily: "var(--font-body)",
              fontSize: "13px",
              fontWeight: 500,
              color: "var(--text-primary)"
            }}>
              {checklist.title}
            </span>
            <StreakBadge streak={checklist.streak} size="sm" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// DASHBOARD SECTION
// Collapsible list of items
// ============================================
function DashboardSection({ title, items, color, onCheckItem }) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();

  if (items.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <SectionHeader
        title={title}
        count={items.length}
        color={color}
        collapsed={collapsed}
        onToggle={() => setCollapsed(prev => !prev)}
      />

      {!collapsed && (
        <div style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          overflow: "hidden",
          animation: "fadeIn var(--transition-base)"
        }}>
          {items.map(item => (
            <div key={item.id}>
              <ChecklistItem
                item={item}
                onCheck={onCheckItem}
                readOnly={false}
                onUpdate={() => {}}
                onDelete={() => {}}
              />
              <div
                onClick={() => navigate("/checklists/" + item.checklistId)}
                style={{
                  padding: "2px var(--space-4) var(--space-2)",
                  cursor: "pointer"
                }}
              >
                <span style={{
                  fontFamily: "var(--font-body)",
                  fontSize: "11px",
                  color: "var(--text-muted)",
                  fontStyle: "italic"
                }}>
                  in {item.checklistTitle} →
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function Dashboard() {
  const { user } = useAuth();
  const { addLocalNotification } = useNotifications();
  const navigate = useNavigate();

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [undoQueue, setUndoQueue] = useState([]);

  // ============================================
  // FETCH CHECKLISTS
  // ============================================
  const fetchChecklists = useCallback(async () => {
    try {
      const res = await fetch("/api/checklists", {
        headers: { "Content-Type": "application/json" }
      });
      const data = await res.json();
      setChecklists(data);
    } catch (err) {
      console.error("Failed to fetch checklists", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  // ============================================
  // CHECK OFF ITEM
  // Optimistic update + undo support
  // ============================================
  async function handleCheckItem(item) {
    // Optimistic update
    setChecklists(prev => prev.map(checklist => {
      if (checklist.id !== item.checklistId) return checklist;
      return {
        ...checklist,
        items: checklist.items.map(i =>
          i.id === item.id ? { ...i, checked: true } : i
        )
      };
    }));

    // Add to undo queue
    const undoItem = { ...item, checklistId: item.checklistId };
    setUndoQueue(prev => [...prev, undoItem]);

    // Show undo toast via notification
    addLocalNotification({
      type: "undo",
      title: "Item checked off",
      message: item.text,
      undoItem
    });

    // Save to API
    try {
      const checklist = checklists.find(c => c.id === item.checklistId);
      if (!checklist) return;

      const updatedItems = checklist.items.map(i =>
        i.id === item.id ? { ...i, checked: true } : i
      );

      await fetch("/api/checklists", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: checklist.id,
          title: checklist.title,
          items: updatedItems,
          category: checklist.category,
          recurring: checklist.recurring,
          recurringInterval: checklist.recurringInterval
        })
      });
    } catch (err) {
      console.error("Failed to update item", err);
    }
  }

  // ============================================
  // DERIVED DATA
  // ============================================
  const { overdue, today, upcoming, noDate } = categorizeItems(checklists);
  const { totalCompleted, bestStreak, completionRate, activeStreaks } = computeStats(checklists);

  const firstName = user?.name?.split(" ")[0] || "there";

  if (loading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh"
      }}>
        <p style={{ fontFamily: "var(--font-body)", color: "var(--text-muted)" }}>
          Loading your checklists...
        </p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: "800px",
      margin: "0 auto",
      padding: "var(--space-6) var(--space-4)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-6)"
    }}>

      {/* Greeting */}
      <div>
        <h1 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 900,
          fontSize: "clamp(28px, 5vw, 40px)",
          color: "var(--text-primary)",
          margin: "0 0 var(--space-2)",
          letterSpacing: "-1px"
        }}>
          {getGreeting()}, {firstName}.
        </h1>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "15px",
          color: "var(--text-muted)",
          margin: 0
        }}>
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric"
          })}
        </p>
      </div>

      {/* Stats row */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: "var(--space-3)"
      }}>
        <StatsCard
          icon="✅"
          label="Completed"
          value={totalCompleted}
          color="var(--accent-green)"
        />
        <StatsCard
          icon="🔥"
          label="Best Streak"
          value={bestStreak}
          suffix="days"
          color="var(--accent-gold)"
        />
        <StatsCard
          icon="📋"
          label="Active Lists"
          value={checklists.length}
          color="var(--accent-green)"
        />
        <StatsCard
          icon="⭐"
          label="Completion"
          value={completionRate}
          suffix="%"
          color="var(--accent-gold)"
        />
      </div>

      {/* Active streaks */}
      <StreakSummary checklists={checklists} />

      {/* Dashboard sections */}
      <DashboardSection
        title="Overdue"
        items={overdue}
        color="var(--status-overdue)"
        onCheckItem={handleCheckItem}
      />
      <DashboardSection
        title="Today"
        items={today}
        color="var(--status-today)"
        onCheckItem={handleCheckItem}
      />
      <DashboardSection
        title="Upcoming"
        items={upcoming}
        color="var(--status-upcoming)"
        onCheckItem={handleCheckItem}
      />
      <DashboardSection
        title="No Date"
        items={noDate}
        color="var(--text-muted)"
        onCheckItem={handleCheckItem}
      />

      {/* Empty state */}
      {overdue.length === 0 && today.length === 0 && upcoming.length === 0 && noDate.length === 0 && (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--space-8)",
          gap: "var(--space-4)",
          textAlign: "center"
        }}>
          <span style={{ fontSize: "64px" }}>🎉</span>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "24px",
            color: "var(--text-primary)",
            margin: 0
          }}>
            All caught up!
          </h2>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "15px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            You have no pending items. Add some checklists to get started.
          </p>
          <button
            onClick={() => navigate("/checklists")}
            style={{
              background: "var(--accent-green)",
              border: "none",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-3) var(--space-6)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15px",
              color: "white",
              transition: "opacity var(--transition-fast)"
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            Go to Checklists
          </button>
        </div>
      )}
    </div>
  );
}